import React, { useState, useRef, useCallback } from 'react';
import {
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider,
  Typography,
  Box,
  useTheme
} from '@mui/material';
import {
  Download as DownloadIcon,
  TableChart as CsvIcon,
  Description as JsonIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  ArrowDropDown as ArrowDropDownIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * Enhanced export utilities with multiple format support
 */
import { 
  arrayToCsv, 
  pivotTableToCsv, 
  downloadCsv, 
  generateTimestampedFilename,
  formatCsvCell 
} from '../../utils/csvExport';

/**
 * Styled components with Waltero branding
 */
const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
  '& .MuiButton-root': {
    backgroundColor: theme.palette.waltero.secondary.primaryApricot,
    color: theme.palette.common.white,
    border: `1px solid ${theme.palette.waltero.secondary.primaryApricot}`,
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: '0.875rem',
    letterSpacing: '0.5px',
    
    '&:hover': {
      backgroundColor: theme.palette.waltero.secondary.darkApricot,
      borderColor: theme.palette.waltero.secondary.darkApricot,
      boxShadow: '0px 4px 12px rgba(195, 93, 44, 0.3)'
    },
    
    '&:disabled': {
      backgroundColor: theme.palette.waltero.primary.neutralBlue,
      color: theme.palette.waltero.primary.mediumBlue,
      borderColor: theme.palette.waltero.primary.neutralBlue
    }
  }
}));

const ExportMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  minHeight: 'auto',
  
  '& .MuiListItemIcon-root': {
    minWidth: '36px',
    color: theme.palette.waltero.primary.lightBlue
  },
  
  '& .MuiListItemText-primary': {
    color: theme.palette.waltero.primary.coreBlue,
    fontWeight: 500
  },
  
  '& .MuiListItemText-secondary': {
    color: theme.palette.waltero.primary.mediumBlue,
    fontSize: '0.75rem'
  },
  
  '&:hover': {
    backgroundColor: theme.palette.waltero.primary.backgroundBlue
  },
  
  '&.Mui-disabled': {
    opacity: 0.5,
    '& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary': {
      color: theme.palette.waltero.primary.mediumBlue
    }
  }
}));

/**
 * Export format configurations
 */
const EXPORT_FORMATS = {
  csv: {
    label: 'CSV',
    description: 'Comma-separated values',
    icon: <CsvIcon />,
    mimeType: 'text/csv',
    extension: 'csv'
  },
  json: {
    label: 'JSON',
    description: 'JavaScript Object Notation',
    icon: <JsonIcon />,
    mimeType: 'application/json',
    extension: 'json'
  },
  pdf: {
    label: 'PDF',
    description: 'Portable Document Format',
    icon: <PdfIcon />,
    mimeType: 'application/pdf',
    extension: 'pdf'
  },
  image: {
    label: 'PNG Image',
    description: 'Export as image',
    icon: <ImageIcon />,
    mimeType: 'image/png',
    extension: 'png'
  }
};

/**
 * Enhanced WalteroExportButton Component
 * 
 * A sophisticated export button with multiple format support:
 * - CSV export with custom formatting
 * - JSON export with structured data
 * - PDF export with table formatting
 * - PNG image export for charts/tables
 * - Progress indication and error handling
 * - Accessibility compliant (WCAG 2.1 AA)
 * - Keyboard navigation support
 * 
 * @param {Object} props - Component properties
 * @param {Array|Object} props.data - Data to export
 * @param {Array} [props.columns] - Column configuration for tabular data
 * @param {string} [props.filename] - Base filename (without extension)
 * @param {Array} [props.formats=['csv']] - Available export formats
 * @param {string} [props.defaultFormat='csv'] - Default export format
 * @param {boolean} [props.split=false] - Show split button with dropdown
 * @param {string} [props.variant='contained'] - Button variant
 * @param {string} [props.size='medium'] - Button size
 * @param {boolean} [props.disabled=false] - Disable button
 * @param {Function} [props.onExportStart] - Callback when export starts
 * @param {Function} [props.onExportComplete] - Callback when export completes
 * @param {Function} [props.onExportError] - Callback when export fails
 * @param {Object} [props.customExporters] - Custom export functions
 * @param {Object} [props.pdfOptions] - PDF export options
 * @param {string} [props.tableType] - Table type for specialized CSV export
 * @param {React.ReactElement} [props.captureElement] - Element to capture for image export
 */
const WalteroExportButton = ({
  data = [],
  columns = [],
  filename,
  formats = ['csv'],
  defaultFormat = 'csv',
  split = false,
  variant = 'contained',
  size = 'medium',
  disabled = false,
  onExportStart,
  onExportComplete,
  onExportError,
  customExporters = {},
  pdfOptions = {},
  tableType,
  captureElement,
  ...otherProps
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const fileInputRef = useRef(null);

  // Generate filename with timestamp
  const getFilename = useCallback((format) => {
    const baseName = filename || 'waltero_export';
    return generateTimestampedFilename(baseName);
  }, [filename]);

  // CSV Export Handler
  const exportToCsv = useCallback(async (exportData) => {
    try {
      let csvContent = '';
      
      if (tableType && customExporters.csv) {
        // Use custom CSV exporter for specific table types
        csvContent = customExporters.csv(exportData);
      } else if (Array.isArray(exportData) && columns.length > 0) {
        // Standard tabular export
        csvContent = arrayToCsv(exportData, columns);
      } else if (typeof exportData === 'object' && exportData.pivotData) {
        // Pivot table export
        csvContent = pivotTableToCsv(
          exportData.pivotData,
          exportData.dates || [],
          exportData.fixedHeaders || [],
          exportData.valueFormatter
        );
      } else {
        // Fallback: convert object to simple CSV
        const headers = Object.keys(exportData[0] || {}).map(key => ({ key, label: key }));
        csvContent = arrayToCsv(Array.isArray(exportData) ? exportData : [exportData], headers);
      }

      if (csvContent) {
        downloadCsv(csvContent, getFilename('csv'));
        return true;
      }
      return false;
    } catch (error) {
      console.error('CSV export failed:', error);
      throw error;
    }
  }, [tableType, customExporters, columns, getFilename]);

  // JSON Export Handler
  const exportToJson = useCallback(async (exportData) => {
    try {
      const jsonContent = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${getFilename('json')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('JSON export failed:', error);
      throw error;
    }
  }, [getFilename]);

  // PDF Export Handler
  const exportToPdf = useCallback(async (exportData) => {
    try {
      const doc = new jsPDF({
        orientation: pdfOptions.orientation || 'landscape',
        unit: 'mm',
        format: pdfOptions.format || 'a4'
      });

      // Add Waltero branding
      doc.setFontSize(20);
      doc.setTextColor(2, 56, 97); // Waltero deep blue
      doc.text('Waltero IoT Dashboard Export', 14, 20);
      
      doc.setFontSize(12);
      doc.setTextColor(35, 103, 139); // Waltero medium blue
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);

      if (Array.isArray(exportData) && columns.length > 0) {
        // Table export
        const tableData = exportData.map(row => 
          columns.map(col => {
            const value = col.accessor ? col.accessor(row) : row[col.id];
            return formatCsvCell(value);
          })
        );

        doc.autoTable({
          head: [columns.map(col => col.label)],
          body: tableData,
          startY: 40,
          styles: {
            fontSize: 8,
            cellPadding: 2
          },
          headStyles: {
            fillColor: [2, 56, 97], // Waltero deep blue
            textColor: [255, 255, 255],
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [253, 243, 235] // Waltero background apricot
          },
          margin: { top: 40, right: 14, bottom: 20, left: 14 }
        });
      } else {
        // Simple content export
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(JSON.stringify(exportData, null, 2), 14, 40);
      }

      doc.save(`${getFilename('pdf')}.pdf`);
      return true;
    } catch (error) {
      console.error('PDF export failed:', error);
      throw error;
    }
  }, [columns, getFilename, pdfOptions]);

  // Image Export Handler
  const exportToImage = useCallback(async () => {
    try {
      if (!captureElement) {
        throw new Error('No capture element specified for image export');
      }

      const canvas = await html2canvas(captureElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${getFilename('image')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');

      return true;
    } catch (error) {
      console.error('Image export failed:', error);
      throw error;
    }
  }, [captureElement, getFilename]);

  // Main export handler
  const handleExport = useCallback(async (format) => {
    if (disabled || exporting) return;

    setExporting(true);
    setExportStatus(null);
    onExportStart?.(format);

    try {
      let success = false;

      switch (format) {
        case 'csv':
          success = await exportToCsv(data);
          break;
        case 'json':
          success = await exportToJson(data);
          break;
        case 'pdf':
          success = await exportToPdf(data);
          break;
        case 'image':
          success = await exportToImage();
          break;
        default:
          if (customExporters[format]) {
            success = await customExporters[format](data);
          } else {
            throw new Error(`Unsupported export format: ${format}`);
          }
      }

      if (success) {
        setExportStatus('success');
        onExportComplete?.(format);
        setTimeout(() => setExportStatus(null), 3000);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      setExportStatus('error');
      onExportError?.(error, format);
      setTimeout(() => setExportStatus(null), 5000);
    } finally {
      setExporting(false);
      setAnchorEl(null);
    }
  }, [
    disabled,
    exporting,
    data,
    exportToCsv,
    exportToJson,
    exportToPdf,
    exportToImage,
    customExporters,
    onExportStart,
    onExportComplete,
    onExportError
  ]);

  // Menu handlers
  const handleMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleMenuItemClick = useCallback((format) => {
    handleExport(format);
  }, [handleExport]);

  // Single format button
  if (!split || formats.length === 1) {
    const format = formats[0] || defaultFormat;
    const formatConfig = EXPORT_FORMATS[format];

    return (
      <Button
        variant={variant}
        size={size}
        disabled={disabled || exporting}
        onClick={() => handleExport(format)}
        startIcon={
          exporting ? (
            <CircularProgress size={16} color="inherit" />
          ) : exportStatus === 'success' ? (
            <CheckIcon />
          ) : exportStatus === 'error' ? (
            <ErrorIcon />
          ) : (
            formatConfig?.icon || <DownloadIcon />
          )
        }
        sx={{
          backgroundColor: theme.palette.waltero.secondary.primaryApricot,
          color: theme.palette.common.white,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          
          '&:hover': {
            backgroundColor: theme.palette.waltero.secondary.darkApricot,
            boxShadow: '0px 4px 12px rgba(195, 93, 44, 0.3)'
          },
          
          '&:disabled': {
            backgroundColor: theme.palette.waltero.primary.neutralBlue,
            color: theme.palette.waltero.primary.mediumBlue
          }
        }}
        {...otherProps}
      >
        {exporting
          ? 'Exporting...'
          : exportStatus === 'success'
          ? 'Exported!'
          : exportStatus === 'error'
          ? 'Failed'
          : `Export ${formatConfig?.label || format.toUpperCase()}`
        }
      </Button>
    );
  }

  // Split button with dropdown
  return (
    <>
      <StyledButtonGroup variant={variant} size={size} disabled={disabled}>
        <Button
          onClick={() => handleExport(defaultFormat)}
          disabled={exporting}
          startIcon={
            exporting ? (
              <CircularProgress size={16} color="inherit" />
            ) : exportStatus === 'success' ? (
              <CheckIcon />
            ) : exportStatus === 'error' ? (
              <ErrorIcon />
            ) : (
              <DownloadIcon />
            )
          }
          {...otherProps}
        >
          {exporting
            ? 'Exporting...'
            : exportStatus === 'success'
            ? 'Exported!'
            : exportStatus === 'error'
            ? 'Failed'
            : 'Export'
          }
        </Button>
        <Button
          size={size}
          onClick={handleMenuOpen}
          disabled={exporting}
          aria-haspopup="menu"
          aria-expanded={menuOpen ? 'true' : 'false'}
          aria-label="Export format options"
        >
          <ArrowDropDownIcon />
        </Button>
      </StyledButtonGroup>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'export-format-button',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            border: `1px solid ${theme.palette.waltero.primary.neutralBlue}`,
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(2, 56, 97, 0.15)'
          }
        }}
      >
        <Box px={2} py={1}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.waltero.primary.mediumBlue,
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}
          >
            Export Formats
          </Typography>
        </Box>
        <Divider />
        
        {formats.map((format) => {
          const formatConfig = EXPORT_FORMATS[format];
          const isDisabled = format === 'image' && !captureElement;
          
          return (
            <ExportMenuItem
              key={format}
              onClick={() => handleMenuItemClick(format)}
              disabled={isDisabled}
            >
              <ListItemIcon>
                {formatConfig?.icon || <DownloadIcon />}
              </ListItemIcon>
              <ListItemText
                primary={formatConfig?.label || format.toUpperCase()}
                secondary={
                  isDisabled 
                    ? 'Not available for this data'
                    : formatConfig?.description || `Export as ${format}`
                }
              />
            </ExportMenuItem>
          );
        })}
      </Menu>
    </>
  );
};

/**
 * Pre-configured export buttons for common use cases
 */

// Standard CSV export button
export const WalteroCsvExportButton = (props) => (
  <WalteroExportButton
    formats={['csv']}
    defaultFormat="csv"
    {...props}
  />
);

// Multi-format export button
export const WalteroMultiExportButton = (props) => (
  <WalteroExportButton
    formats={['csv', 'json', 'pdf']}
    defaultFormat="csv"
    split={true}
    {...props}
  />
);

// Chart/Image export button
export const WalteroChartExportButton = (props) => (
  <WalteroExportButton
    formats={['image', 'pdf']}
    defaultFormat="image"
    split={true}
    {...props}
  />
);

export default WalteroExportButton;